/**
 * Booking Timeline view-model mapper.
 *
 * Maps raw booking + payment data into a list of timeline steps
 * for the BookingTimeline UI component.
 *
 * Payment state is derived from aggregated payment amounts —
 * never from the stale paymentStatus column alone.
 */

export type TimelineStepStatus = 'completed' | 'current' | 'upcoming' | 'cancelled';

export interface TimelineStep {
  key: string;
  label: string;
  description?: string;
  date?: string;
  status: TimelineStepStatus;
}

export interface BookingTimelineInput {
  bookingStatus: string; // 'pending' | 'reserved_pending_payment' | 'confirmed' | 'completed' | 'cancelled'
  paymentStatus: string; // 'none' | 'pending' | 'paid' | 'failed' | 'refunded'
  requiresPrepayment: boolean;
  /** Aggregated from accommodation_booking_payments */
  amountPaidOre?: number;
  /** Total stay price in öre (null = unknown) */
  totalStayOre?: number | null;
  createdAt: string;
  checkIn: string;
  checkOut: string;
  cancelledAt?: string | null;
  paidAt?: string | null;
}

/**
 * Determine whether the stay is in the past based on check-out date.
 */
function isStayCompleted(checkOut: string): boolean {
  return new Date(checkOut) < new Date();
}

/**
 * Derive payment completeness from aggregated amounts.
 * Falls back to paymentStatus when totalStayOre is unknown.
 */
function derivePaymentState(input: BookingTimelineInput): { isPaid: boolean; hasPayment: boolean } {
  const { amountPaidOre = 0, totalStayOre, paymentStatus } = input;
  const totalOre = totalStayOre ?? 0;

  const isPaid = totalOre > 0
    ? amountPaidOre >= totalOre
    : paymentStatus === 'paid';

  const hasPayment = amountPaidOre > 0;

  return { isPaid, hasPayment };
}

/**
 * Map raw booking/payment data to an ordered list of timeline steps.
 */
export function mapBookingToTimeline(input: BookingTimelineInput): TimelineStep[] {
  const {
    bookingStatus, requiresPrepayment,
    createdAt, checkIn, checkOut, cancelledAt, paidAt,
  } = input;

  const isCancelled = bookingStatus === 'cancelled';
  const isConfirmed = bookingStatus === 'confirmed' || bookingStatus === 'completed';
  const isReserved = bookingStatus === 'reserved_pending_payment';
  const isPending = bookingStatus === 'pending';

  const { isPaid, hasPayment } = derivePaymentState(input);
  const stayDone = isConfirmed && isStayCompleted(checkOut);

  // Step 1: Förfrågan skickad — always completed once a booking exists
  const step1: TimelineStep = {
    key: 'request',
    label: 'Förfrågan skickad',
    date: createdAt,
    status: 'completed',
  };

  // Step 2: Bokning bekräftad / reserverad
  const step2: TimelineStep = (() => {
    if (isCancelled) {
      return {
        key: 'confirmed',
        label: 'Bokning bekräftad',
        status: 'cancelled' as TimelineStepStatus,
        description: 'Bokningen avbokades',
        date: cancelledAt ?? undefined,
      };
    }
    if (isConfirmed) {
      return {
        key: 'confirmed',
        label: 'Bokning bekräftad',
        status: 'completed' as TimelineStepStatus,
      };
    }
    if (isReserved) {
      return {
        key: 'confirmed',
        label: 'Bokning reserverad',
        status: 'completed' as TimelineStepStatus,
        description: 'Reserverad i väntan på betalning',
      };
    }
    // pending
    return {
      key: 'confirmed',
      label: 'Bokning bekräftad',
      status: 'current' as TimelineStepStatus,
      description: 'Värden granskar din förfrågan',
    };
  })();

  // Step 3: Betalning — derived from aggregated payment amounts
  const step3: TimelineStep = (() => {
    if (isCancelled) {
      return {
        key: 'payment',
        label: 'Betalning',
        status: 'cancelled' as TimelineStepStatus,
      };
    }
    if (isPaid) {
      return {
        key: 'payment',
        label: 'Betalning',
        status: 'completed' as TimelineStepStatus,
        description: 'Betald',
        date: paidAt ?? undefined,
      };
    }
    if (isReserved) {
      return {
        key: 'payment',
        label: 'Betalning',
        status: 'current' as TimelineStepStatus,
        description: hasPayment ? 'Delvis betald' : 'Betalning inväntas',
      };
    }
    if (isConfirmed && (requiresPrepayment || hasPayment)) {
      return {
        key: 'payment',
        label: 'Betalning',
        status: 'current' as TimelineStepStatus,
        description: hasPayment ? 'Delvis betald' : 'Betalning inväntas',
      };
    }
    if (isConfirmed) {
      return {
        key: 'payment',
        label: 'Betalning',
        status: 'current' as TimelineStepStatus,
        description: 'Betalning hanteras utanför plattformen',
      };
    }
    // pending
    return {
      key: 'payment',
      label: 'Betalning',
      status: 'upcoming' as TimelineStepStatus,
      description: 'Hanteras efter bekräftelse',
    };
  })();

  // Step 4: Vistelsen genomförd
  const step4: TimelineStep = {
    key: 'stay',
    label: 'Vistelsen genomförd',
    status: isCancelled
      ? 'cancelled'
      : stayDone
        ? 'completed'
        : 'upcoming',
    date: stayDone ? checkOut : undefined,
  };

  return [step1, step2, step3, step4];
}
